import React from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, MapPin } from 'lucide-react';
import { getProfessionalIdentity } from '../config/professionalIdentity';
import socialLinks from '../config/socialLinks';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';
import AdminFooterHealthPulse from './AdminFooterHealthPulse';

// SVG Icons for social platforms (clean, branded)
const SocialIcon = ({ platform, className = "h-5 w-5" }) => {
  const icons = {
    instagram: (
      <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
      </svg>
    ),
    facebook: (
      <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
      </svg>
    ),
    tiktok: (
      <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
      </svg>
    ),
    youtube: (
      <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path fillRule="evenodd" d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z" clipRule="evenodd" />
      </svg>
    ),
    patreon: (
      <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M15.386.524c-4.764 0-8.64 3.876-8.64 8.64 0 4.75 3.876 8.613 8.64 8.613 4.75 0 8.614-3.864 8.614-8.613C24 4.4 20.136.524 15.386.524M.003 23.537h4.22V.524H.003"/>
      </svg>
    ),
  };
  
  return icons[platform] || null;
};

const Footer = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  const isAdminRoute = location.pathname.includes('/admin');
  
  // Get bilingual professional identity
  const PROFESSIONAL_IDENTITY = getProfessionalIdentity(language);

  if (isAdminRoute) {
    return null;
  }

  return (
    <footer className="bg-gray-900 text-gray-300 border-t border-gray-800">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src={PROFESSIONAL_IDENTITY.logoUrl}
                alt={PROFESSIONAL_IDENTITY.organization}
                className="h-12 w-auto object-contain"
              />
              <div>
                <h2 className="text-xl font-bold text-white">{PROFESSIONAL_IDENTITY.organization}</h2>
                <p className="text-sm text-gray-400">{t('footer.tagline')}</p>
              </div>
            </div>
            <p className="text-gray-400 mb-4 max-w-md">
              {t('footer.description')}
            </p>
            
            {/* Social Media Links */}
            <div className="flex items-center gap-4 mt-6" data-testid="footer-social-links">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                  aria-label={`Follow us on ${social.name}`}
                  data-testid={`social-link-${social.icon}`}
                >
                  <SocialIcon platform={social.icon} className="h-5 w-5" />
                </a>
              ))}
            </div>
            
            <div className="text-sm text-gray-500 mt-6">
              <p className="font-semibold text-gray-300">{PROFESSIONAL_IDENTITY.fullNameWithDr}</p>
              <p>{PROFESSIONAL_IDENTITY.title}</p>
              <p>{PROFESSIONAL_IDENTITY.titleLine2}</p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li>
                <a href="/#topics" className="text-gray-400 hover:text-orange-500 transition-colors">
                  {t('footer.browseTopics')}
                </a>
              </li>
              <li>
                <a 
                  href="/academy/sleep-reset" 
                  className="text-gray-400 hover:text-orange-500 transition-colors"
                  data-testid="footer-academy-learn"
                >
                  {PROFESSIONAL_IDENTITY.academyName}
                </a>
              </li>
              <li>
                <a href="/membership" className="text-gray-400 hover:text-orange-500 transition-colors">
                  {t('nav.membership')}
                </a>
              </li>
              <li>
                <a href="/question-banks" className="text-gray-400 hover:text-orange-500 transition-colors">
                  {t('nav.questionBanks')}
                </a>
              </li>
              <li>
                <a href="/organizations" className="text-gray-400 hover:text-orange-500 transition-colors">
                  {t('nav.organizations')}
                </a>
              </li>
              <li>
                <a href="/research" className="text-gray-400 hover:text-orange-500 transition-colors">
                  {t('nav.research')}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('footer.contact')}</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-gray-400">
                <MapPin className="h-4 w-4 text-blue-500 flex-shrink-0" />
                <span>{PROFESSIONAL_IDENTITY.location}</span>
              </li>
              <li className="flex items-center gap-2 text-gray-400">
                <Mail className="h-4 w-4 text-blue-500 flex-shrink-0" />
                <a href="mailto:info@sleephealthservices.org" className="hover:text-orange-500 transition-colors">
                  info@sleephealthservices.org
                </a>
              </li>
            </ul>
            
            {/* Secondary Social Links with labels for Contact Section */}
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-400 mb-3">{t('footer.followUs')}</h4>
              <div className="flex flex-wrap gap-3">
                {socialLinks.slice(0, 3).map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-white transition-colors"
                  >
                    <SocialIcon platform={social.icon} className="h-4 w-4" />
                    <span>{social.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {new Date().getFullYear()} {PROFESSIONAL_IDENTITY.organization}. {t('footer.copyright').split('. ').slice(1).join('. ')}
              {' · '}
              <a href="/legal/privacy" className="underline hover:text-white" data-testid="footer-privacy-link">{language === 'es' ? 'Privacidad' : 'Privacy'}</a>
              {' · '}
              <a href="/legal/sub-processors" className="underline hover:text-white" data-testid="footer-subprocessors-link">{language === 'es' ? 'Subprocesadores' : 'Sub-processors'}</a>
            </p>

            <div className="flex items-center gap-4">
              {/* Admin-only operational pulse — invisible to all other users */}
              <AdminFooterHealthPulse />

              {/* Social Icons Row - Mobile Friendly */}
              <div className="flex items-center gap-4 md:hidden">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-white transition-colors"
                    aria-label={social.name}
                  >
                    <SocialIcon platform={social.icon} className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>
          <p className="text-gray-500 text-xs mt-4 text-center">
            {t('footer.disclaimer')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
